import { LayerProps } from "react-map-gl";
import { ANIA_ENUM } from "../util/AniaEnums";
import { nasaPrecipitationColourMapping, nasaPrecipitationColors } from "../util/colors";

const MAX_ZOOM_LEVEL = 9;

export const dataLayer = {
  id: "data",
  type: "line",
  paint: {
    "line-color": "rgba(252, 0, 0, 0.5)",
    "line-width": 5,
  },
};

export const usSeismicPaintLayer = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "fill",
  source: "seismicClassificationUSRiskSource",
  paint: {
    "fill-color": [
      "interpolate",
      ["linear"],
      ["get", "cys_seismic_zone"],
      1,
      "#ecf0f1",
      2,
      "#ecf0f1",
      3,
      "#ecf0f1",
      4,
      "#ecf0f1",
      5,
      "#3498db",
      6,
      "#3498db",
      7,
      "#3498db",
      8,
      "#3498db",
      9,
      "#2ecc71",
      10,
      "#2ecc71",
      11,
      "#2ecc71",
      12,
      "#2ecc71",
      13,
      "#2ecc71",
      14,
      "#2ecc71",
      15,
      "#2ecc71",
      16,
      "#2ecc71",
      17,
      "#f1c40f",
      18,
      "#f1c40f",
      19,
      "#f1c40f",
      20,
      "#f1c40f",
      21,
      "#f1c40f",
      22,
      "#f1c40f",
      23,
      "#f1c40f",
      24,
      "#f1c40f",
      25,
      "#f1c40f",
      26,
      "#f1c40f",
      27,
      "#f1c40f",
      28,
      "#f1c40f",
      29,
      "#f1c40f",
      30,
      "#f1c40f",
      31,
      "#f1c40f",
      32,
      "#f1c40f",
      33,
      "#f39c12",
      34,
      "#f39c12",
      35,
      "#f39c12",
      36,
      "#f39c12",
      37,
      "#f39c12",
      38,
      "#f39c12",
      39,
      "#f39c12",
      40,
      "#f39c12",
      41,
      "#f39c12",
      42,
      "#f39c12",
      43,
      "#f39c12",
      44,
      "#f39c12",
      45,
      "#f39c12",
      46,
      "#f39c12",
      47,
      "#f39c12",
      48,
      "#f39c12",
      49,
      "#d35400",
      50,
      "#d35400",
      51,
      "#d35400",
      52,
      "#d35400",
      53,
      "#d35400",
      54,
      "#d35400",
      55,
      "#d35400",
      56,
      "#d35400",
      57,
      "#d35400",
      58,
      "#d35400",
      59,
      "#d35400",
      60,
      "#d35400",
      61,
      "#d35400",
      62,
      "#d35400",
      63,
      "#d35400",
      64,
      "#d35400",
      65,
      "#c0392b",
      80,
      "#c0392b",
    ],
    "fill-opacity": 0.75,
  },
};

export const italyPaintLayer = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "fill",
  source: "seismicClassificationITARiskSource",
  paint: {
    "fill-color": [
      "interpolate",
      ["linear"],
      ["get", "cys_seismic_zone"],
      1,
      "#F2F12D",
      2,
      "#CA8323",
      3,
      "#A25626",
      4,
      "#723122",
    ],
    "fill-opacity": 0.75,
  },
};

export const explosionLayer = {
  //maxzoom: MAX_ZOOM_LEVEL,
  type: "fill",
  source: "explosion",
  paint: {
    "fill-color": [
      "interpolate",
      ["linear"],
      ["get", "explosionColorLevel"],
      1,
      "#fff5f0",
      2,
      "#fca486",
      3,
      "#ea372a",
      4,
      "#67000d",
    ],
    "fill-opacity": 0.55,
  },
};

export const explosionCircleLayer = {
  id: "explosionCircleLayer",
  minzoom: 9,
  type: "circle",
  source: "explosionCenter",
  paint: {
    "circle-radius": 5,

    "circle-color": "rgb(178,24,43)",
  },
};

export const rainfallLevelLayer: LayerProps = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "fill",
  source: "landslideData",
  paint: {
    "fill-color": [
      "interpolate",
      ["linear"],
      ["get", "precip"],
      1,
      "#C0C0C0",
      2,
      "#12831E",
      3,
      "#138B50",
      5,
      "#1CB138",
      10,
      "#5DCE24",
      20,
      "#B7E529",
      40,
      "#FAF32F",
      70,
      "#FBC327",
      120,
      "#FE921F",
      200,
      "#FC0110",
      350,
      "#C7000A",
      600,
      "#8E0005",
    ],
    "fill-opacity": 0.4,
  },
};

export const heatmapLayer: LayerProps = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "heatmap",
  source: "earchquakes",
  paint: {
    // Increase the heatmap weight based on frequency and property magnitude
    "heatmap-weight": ["interpolate", ["linear"], ["get", "mag"], 0, 0, 6, 1],
    // Increase the heatmap color weight weight by zoom level
    // heatmap-intensity is a multiplier on top of heatmap-weight
    "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, MAX_ZOOM_LEVEL, 3],
    // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
    // Begin color ramp at 0-stop with a 0-transparency color
    // to create a blur-like effect.
    "heatmap-color": [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      0,
      "rgba(33,102,172,0)",
      0.2,
      "rgb(103,169,207)",
      0.4,
      "rgb(209,229,240)",
      0.6,
      "rgb(253,219,199)",
      0.8,
      "rgb(239,138,98)",
      1,
      "rgb(178,24,43)",
    ],
    // Adjust the heatmap radius by zoom level
    "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 2, MAX_ZOOM_LEVEL, 20],
    // Transition from heatmap to circle layer by zoom level
    "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 7, 1, 9, 0],
  },
};

export const environmentDistanceFireStationLayerColors = ["blue", "green", "yellow", "red", "gray"];

export const environmentDistanceFacilityLayer: LayerProps = {
  // I need here a layer of type "line". The data source is a geojson with lines representing routes to fire stations.
  type: "line",
  // The color should be computed based on GeoJson Line polyline property "distanceLevel"
  source: "EnvironmentDistanceFireStationSource",
  paint: {
    "line-color": [
      "match",
      ["get", "distanceLevel"],
      1,
      environmentDistanceFireStationLayerColors[0],
      2,
      environmentDistanceFireStationLayerColors[1],
      3,
      environmentDistanceFireStationLayerColors[2],
      4,
      environmentDistanceFireStationLayerColors[3],
      environmentDistanceFireStationLayerColors[4], // fallback color
    ],
    "line-width": ["match", ["get", "distanceLevel"], 1, 4, 2, 6, 3, 8, 4, 10, 12], // to avoid exact overlap, they have different widths
    "line-opacity": 0.7,
  },
  beforeId: "country-label", // roads from street style map should not overlap the routes we draw, so put it above country label
};

export const USHeatmapLayer = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "heatmap",
  source: "USEarchquakes",
  paint: {
    // Increase the heatmap weight based on frequency and property magnitude
    "heatmap-weight": ["interpolate", ["linear"], ["get", "mag"], 0, 0, 6, 1],
    // Increase the heatmap color weight weight by zoom level
    // heatmap-intensity is a multiplier on top of heatmap-weight
    "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, MAX_ZOOM_LEVEL, 3],
    // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
    // Begin color ramp at 0-stop with a 0-transparency color
    // to create a blur-like effect.
    "heatmap-color": [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      0,
      "rgba(33,102,172,0)",
      0.2,
      "rgb(103,169,207)",
      0.4,
      "rgb(209,229,240)",
      0.6,
      "rgb(253,219,199)",
      0.8,
      "rgb(239,138,98)",
      1,
      "rgb(178,24,43)",
      // 2.74,
      // "rgb(178,24,48)",
    ],
    // Adjust the heatmap radius by zoom level
    "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 2, MAX_ZOOM_LEVEL, 20],
    // Transition from heatmap to circle layer by zoom level
    "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 7, 1, 9, 0],
  },
};
export const aniaLayerColourMapping = [1.0, "#21bf50", 2.0, "#f1d55d", 3.0, "#e07069"];
export const aniaLayerColourMappingFiveLevels = [
  1.0,
  "#4d6dbd",
  2.0,
  "#509bc7",
  3.0,
  "#f1d55d",
  4.0,
  "#e07069",
  5.0,
  "#a7243d",
];

export const ItaAniaWindLevelLayer = (aniaMapIdx: number): LayerProps => {
  if (
    aniaMapIdx === ANIA_ENUM.ANIA_01 ||
    aniaMapIdx === ANIA_ENUM.ANIA_02 ||
    aniaMapIdx === ANIA_ENUM.ANIA_03 ||
    aniaMapIdx === ANIA_ENUM.ANIA_04 ||
    aniaMapIdx === ANIA_ENUM.ANIA_05
  ) {
    let propertyName;
    // Design note: make sure that the propertyName is the same as the one used in the geojson file
    if (aniaMapIdx === ANIA_ENUM.ANIA_01) {
      propertyName = "CartaPericolositaDelVento";
    }
    if (aniaMapIdx === ANIA_ENUM.ANIA_02) {
      propertyName = "Grandine";
    }
    if (aniaMapIdx === ANIA_ENUM.ANIA_03) {
      propertyName = "Fulmini";
    }
    if (aniaMapIdx === ANIA_ENUM.ANIA_04) {
      propertyName = "Precipitazione_75";
    }
    if (aniaMapIdx === ANIA_ENUM.ANIA_05) {
      propertyName = "Precipitazione_nevosa_30";
    }

    return {
      maxzoom: MAX_ZOOM_LEVEL,
      type: "fill",
      source: "ItaAniaWindLevelSourceId",
      paint: {
        "fill-color": ["step", ["get", propertyName], "#ffffff", ...aniaLayerColourMapping],
        "fill-opacity": 0.4,
      },
    };
  } else {
    console.error("Invalid ANIA map index ", aniaMapIdx);
    return {} as LayerProps;
  }
};

export const femaFloodMapLayer = {
  id: "floodplain-fill",
  type: "fill",
  source: "femaFloodMapSource",
  // "source-layer": "california",
  paint: {
    "fill-color": [
      "match",
      ["get", "fld_zone"],
      "A",
      "#B5E2FA",
      "AE",
      "#B5E2FA",
      "AH",
      "#B5E2FA",
      "AR",
      "#B5E2FA",
      "A99",
      "#B5E2FA",
      "A1-30",
      "#B5E2FA",
      "V1-30",
      "#FFBF66",
      "VE",
      "#FFBF66",
      "V",
      "#FFBF66",
      "C",
      "#D7EAC7",
      "B",
      "#D7EAC7",
      "X",
      "#D7EAC7",
      "D",
      "#EAE7E0",
      "#EAE7E0",
    ],
    "fill-opacity": 0.75,
    "fill-outline-color": "white",
  },
};

export const landslideLayer: LayerProps = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "fill",
  source: "landslideData",
  paint: {
    "fill-color": ["interpolate", ["linear"], ["get", "nowcast"], 1, "yellow", 2, "red"],
    "fill-opacity": 0.75,
  },
};

const cocoColorList = [0, "#f9f9f9", 0.01, "#06C626", 1, "#FFFA00", 2, "#D91C15"];
// white green yellow red

export const cocoLayerSeismicCCT: LayerProps = {
  type: "fill",
  source: "CCT",
  paint: {
    "fill-color": ["interpolate", ["linear"], ["get", "CCT"], ...cocoColorList],
  },
};

export const cocoLayerFloodCCA: LayerProps = {
  type: "fill",
  source: "CCT",
  paint: {
    "fill-color": ["interpolate", ["linear"], ["get", "CCA"], ...cocoColorList],
  },
};

export const cocoLayerLandslideCCF: LayerProps = {
  type: "fill",
  source: "CCT",
  paint: {
    "fill-color": ["interpolate", ["linear"], ["get", "CCF"], ...cocoColorList],
  },
};

export const proCivileLayerColourMapping = [0.0, "green", 1.0, "yellow", 2.0, "orange", 3.0, "red"];
// These colors are defined by Protezione Civile, should not be changed.

export const proCivileLayer = (fieldName: string): LayerProps => {
  return {
    maxzoom: MAX_ZOOM_LEVEL,
    type: "fill",
    source: "ProCivileSourceId",

    paint: {
      "fill-color": ["step", ["get", fieldName], "#ffffff", ...proCivileLayerColourMapping],
      "fill-opacity": 0.4,
    },
  };
};

export const crimeDatasetsSweColors = [
  4108,
  "#ffffff",
  7780,
  "#ffbfbf",
  9092,
  "#ff8080",
  10294,
  "#ff4040",
  11840,
  "#ff0000",
];
// 33032,

export const crimeDatasetsSweLayer: LayerProps = {
  maxzoom: MAX_ZOOM_LEVEL,
  type: "fill",
  source: "CrimeDatasetsSweSourceId",

  paint: {
    "fill-color": ["step", ["get", "Per 100 000 inv."], "#ffffff", ...crimeDatasetsSweColors],
    "fill-opacity": 1,
  },
};

export const nasaPrecipitationMatcherEntries = nasaPrecipitationColourMapping.flatMap((entry) => [
  entry[0],
  entry[1],
]);

export const nasaPrecipitationColorsLayer: LayerProps = {
  type: "fill",
  source: "nasaPrecipitationColorsLayerId",
  paint: {
    "fill-color": [
      "match",
      ["get", "value"],
      ...nasaPrecipitationMatcherEntries,
      nasaPrecipitationColors[10],
    ],
  },
};

export const landslideColourMappingFiveLevels = [
  // Measured in per-thousand probability of landslide occurrence
  0,
  nasaPrecipitationColors[0],
  100,
  nasaPrecipitationColors[0],
  200,
  nasaPrecipitationColors[2],
  300,
  nasaPrecipitationColors[3],
  400,
  nasaPrecipitationColors[4],
  500,
  nasaPrecipitationColors[5],
  600,
  nasaPrecipitationColors[6],
  700,
  nasaPrecipitationColors[7],
  800,
  nasaPrecipitationColors[8],
  900,
  nasaPrecipitationColors[9],
  1000,
  nasaPrecipitationColors[10],
  nasaPrecipitationColors[10],
];

export const nasaLandslideColorsLayer: LayerProps = {
  type: "fill",
  source: "nasaLandslideColorsLayerId",
  paint: {
    "fill-color": ["match", ["get", "value"], ...landslideColourMappingFiveLevels],
  },
};
